import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Tabs } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import styled from "styled-components";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { useSubjectiveTestDetail } from "../../../DRS/Context/MyBatchContext";
import EvaluationHeader from "./EvaluationHeader";
import SubjectContent from "./SubjectContent";
import QuestionEvaluationModal from "./QuestionEvaluationModal";
import {
  Question,
  Section,
  SubjectSummary,
  TestDetailResponse,
} from "../types";

// --- Styled Components ---

const EvaluationContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 20px;
  width: 100%;
  margin: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  height: calc(100vh - 100px) !important;
  overflow-x: auto;
  &:hover {
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12);
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px !important;
    &::before {
      border-bottom: 1px solid #f0f0f0;
    }
  }
  .ant-tabs-tab {
    padding: 12px 16px !important;
    margin: 0 !important;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    border-radius: 8px 8px 0 0;

    &-active {
      background: #f0f7ff;
      .ant-tabs-tab-btn {
        color: #1890ff !important;
        font-weight: 700 !important;
      }
    }

    &:hover {
      color: #1890ff !important;
      background: #f5f5f5;
    }
  }
  .ant-tabs-ink-bar {
    height: 3px !important;
    border-radius: 3px 3px 0 0;
    background: #1890ff !important;
  }
`;

const SubjectBadge = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.2s;

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => props.color || "#1890ff"};
    box-shadow: 0 0 0 2px
      ${(props) => (props.color ? props.color + "20" : "#1890ff20")};
  }

  span {
    font-size: 14px;
    letter-spacing: 0.2px;
  }

  .count {
    background: #ff4d4f;
    color: white;
    font-size: 11px;
    padding: 0 8px;
    height: 18px;
    display: flex;
    align-items: center;
    border-radius: 10px;
    font-weight: 600;
  }
`;

// --- Component ---

const StudentQuestionComponent = forwardRef(
  (
    {
      sectionSubmissionId,
      initialData,
      onTimeUp,
      totalMarks,
      studentTestStatus,
      onQuestionClick,
    }: {
      sectionSubmissionId?: number;
      testScheduleId: number;
      onSaveSuccess?: () => void;
      initialData?: TestDetailResponse;
      onTimeUp?: () => void;
      totalMarks: number;
      studentTestStatus?: string;
      onQuestionClick?: (q: Question) => void;
    },
    ref
  ) => {
    const { screen } = useWindowSize();
    const isMobile = ["sm", "md"].includes(screen);
    const { testDetail } = useSubjectiveTestDetail();
    const [localData, setLocalData] = useState<TestDetailResponse | null>(null);
    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
      null
    );
    const [isModalVisible, setIsModalVisible] = useState(false);

    useImperativeHandle(ref, () => ({
      getEvaluationData: () => localData,
    }));

    useEffect(() => {
      if (sectionSubmissionId && !initialData) {
        // fetchSubjectiveTestDetail(sectionSubmissionId);
      }
    }, [sectionSubmissionId, initialData]);

    useEffect(() => {
      const data = initialData || (testDetail as TestDetailResponse);
      if (data) {
        setLocalData(data);
        // Default to first question on mobile
        if (
          isMobile &&
          data.data?.questions?.length > 0 &&
          selectedQuestionId === null
        ) {
          setSelectedQuestionId(data.data.questions[0].id);
        }
      }
    }, [testDetail, initialData, isMobile]);

    const {
      sections = [],
      questions = [],
      obtainedMarks,
      remainingLockTimeInSeconds = (localData?.data?.initialTimeInMinutes ||
        0) * 60,
    } = localData?.data || {};
    const displayData = useMemo(() => {
      if (!localData) return null;

      const totalScore = sections.reduce(
        (acc: number, s: Section) => acc + (s.obtainedMarks || 0),
        0
      );
      const maxTotalScore = sections.reduce(
        (acc: number, s: Section) => acc + (s.totalMarks || 0),
        0
      );

      const topLevelSections = sections.filter(
        (s: Section) => s.parentSectionId === 0 || !s.parentSectionId
      );
      const subjects: SubjectSummary[] = topLevelSections.map(
        (parent: Section) => {
          const childSections = sections.filter(
            (s: Section) => s.parentSectionId === parent.sectionId
          );

          const nestedSections: Section[] =
            childSections.length > 0
              ? childSections.map((child: Section) => ({
                  ...child,
                  sectionSubmissionId: child.id,
                  sectionName: child.title,
                  totalMarks: child.totalMarks,
                  finalMarks: child.finalMarks,
                  sectionComment: child.sectionComment || "",
                  questions: questions
                    .filter((q: Question) => q.sectionId === child.sectionId)
                    .map((q: Question) => ({
                      ...q,
                      questionNumber: q.questionNumber || q.id,
                      questionCode: q.questionCode || `#Q-${q.id}`,
                    })),
                }))
              : [
                  {
                    ...parent,
                    sectionSubmissionId: parent.id,
                    sectionName: parent.title,
                    totalMarks: parent.totalMarks,
                    obtainedMarks: parent.obtainedMarks,
                    sectionComment: parent.sectionComment || "",
                    questions: questions
                      .filter((q: Question) => q.sectionId === parent.sectionId)
                      .map((q: Question) => ({
                        ...q,
                        questionNumber: q.questionNumber || q.id,
                        questionCode: q.questionCode || `#Q-${q.id}`,
                        questionSubmissionId: q.id || q.questionSubmissionId,
                      })),
                  },
                ];

          // Get all questions in the sections of this subject
          const subjectQuestions = nestedSections.flatMap(
            (sec: Section) => sec.questions || []
          );

          // Find evaluatable questions (exclude parent questions)
          const evaluatableQuestions = subjectQuestions.filter(
            (q: Question) => {
              const isParent = subjectQuestions.some(
                (otherQ: Question) => otherQ.parentQuestionId === q.questionId
              );
              return !isParent;
            }
          );

          // Count ungraded questions (exclude questions that are marked as Not Attempted)
          const ungradedCount = evaluatableQuestions.filter(
            (q: Question) =>
              (q.obtainedMarks === null || q.obtainedMarks === undefined) &&
              q.isAttempted !== false
          ).length;

          // Determine dot color
          // Green = Graded (if all questions are evaluated)
          // Yellow = Not Graded (if some questions are not evaluated)
          // Red = Challenged (if the test status is CHALLENGED)
          let statusColor = "#faad14"; // Default Yellow (Not Graded)
          const currentStatus = (studentTestStatus || "").toUpperCase();

          if (currentStatus === "CHALLENGED") {
            statusColor = "#ff4d4f"; // Red
          } else if (ungradedCount === 0) {
            statusColor = "#52c41a"; // Green
          }

          const unreadCount = ungradedCount > 0 ? ungradedCount : undefined;

          return {
            key: String(parent.id),
            label: parent.title,
            statusColor,
            unreadCount,
            sections: nestedSections,
            sectionComment: parent.sectionComment || "",
          };
        }
      );

      return { totalScore, maxTotalScore, subjects };
    }, [localData, sections, questions, studentTestStatus]);

    const handleMarksChange = (
      sectionSubmissionId: number,
      questionId: number,
      partId: number | null,
      value: number | null
    ) => {
      if (!localData) return;

      setLocalData((prev: any) => {
        if (!prev) return prev;

        const updatedQuestions = prev.data.questions.map((q: Question) => {
          if (q.id === questionId || q.questionSubmissionId === questionId) {
            const updatedQ = { ...q };
            if (partId !== null && updatedQ.parts) {
              const updatedParts = updatedQ.parts.map((p) =>
                p.id === partId ? { ...p, marks: value } : p
              );
              const totalObtained = updatedParts.reduce(
                (acc, p) => acc + (p.marks || 0),
                0
              );
              return {
                ...updatedQ,
                parts: updatedParts,
                obtainedMarks: totalObtained,
              };
            } else {
              return { ...updatedQ, obtainedMarks: value };
            }
          }
          return q;
        });

        const updatedSections = prev.data.sections.map((s: Section) => {
          if (s.id === sectionSubmissionId) {
            const sectionQuestions = updatedQuestions.filter(
              (q: Question) => q.sectionId === s.sectionId
            );
            const sectionTotal = sectionQuestions.reduce(
              (acc: number, q: Question) => acc + (q.obtainedMarks || 0),
              0
            );
            return { ...s, obtainedMarks: sectionTotal };
          }
          return s;
        });

        return {
          ...prev,
          data: {
            ...prev.data,
            questions: updatedQuestions,
            sections: updatedSections,
          },
        };
      });
    };

    const handleCommentChange = (
      sectionSubmissionId: number,
      questionId: number | null,
      value: string
    ) => {
      if (!localData) return;
      setLocalData((prev: any) => {
        if (!prev) return prev;

        if (questionId === null) {
          console.log("sectionSubmissionId====", sectionSubmissionId, prev);
          const updatedSections = prev.data.sections.map((s: Section) =>
            s.id === sectionSubmissionId ? { ...s, sectionComment: value } : s
          );
          return {
            ...prev,
            data: { ...prev.data, sections: updatedSections },
          };
        } else {
          const updatedQuestions = prev.data.questions.map((q: Question) =>
            q.id === questionId || q.questionSubmissionId === questionId
              ? { ...q, evaluatedComment: value }
              : q
          );
          return {
            ...prev,
            data: { ...prev.data, questions: updatedQuestions },
          };
        }
      });
    };

    const handleAttemptedChange = (questionId: number, value: boolean) => {
      if (!localData) return;
      setLocalData((prev: any) => {
        if (!prev) return prev;
        const updatedQuestions = prev.data.questions.map((q: any) =>
          q.id === questionId || q.questionSubmissionId === questionId
            ? { ...q, isAttempted: value }
            : q
        );
        return {
          ...prev,
          data: { ...prev.data, questions: updatedQuestions },
        };
      });
    };

    const handleVerifyAIChange = (questionId: number, value: boolean) => {
      if (!localData) return;
      setLocalData((prev: any) => {
        if (!prev) return prev;
        const updatedQuestions = prev.data.questions.map((q: any) =>
          q.id === questionId || q.questionSubmissionId === questionId
            ? { ...q, verifyAIScore: value }
            : q
        );
        return {
          ...prev,
          data: { ...prev.data, questions: updatedQuestions },
        };
      });
    };

    const handleAIEvaluatedMarksChange = (
      sectionSubmissionId: number,
      questionId: number,
      partId: number | null,
      value: number | null
    ) => {
      if (!localData) return;

      setLocalData((prev: any) => {
        if (!prev) return prev;

        const updatedQuestions = prev.data.questions.map((q: Question) => {
          if (q.id === questionId || q.questionSubmissionId === questionId) {
            return { ...q, aiEvaluatedMarks: value };
          }
          return q;
        });

        return {
          ...prev,
          data: {
            ...prev.data,
            questions: updatedQuestions,
          },
        };
      });
    };

    const getStatus = (q: Question) => {
      // Check if this is a parent question (paragraph / question stem) and all its children have obtained marks
      const children = questions.filter(
        (child: Question) => child.parentQuestionId === q.questionId
      );
      if (children.length > 0) {
        const allChildrenGraded = children.every(
          (child: Question) =>
            (child.obtainedMarks !== null &&
              child.obtainedMarks !== undefined) ||
            child.isAttempted
        );
        if (allChildrenGraded) {
          return "full";
        }
      }

      if (q.isAttempted) {
        return "full";
      } else if (q.isAttempted === false) {
        return "unattempted";
      } else if (q.obtainedMarks === q.totalMarks) return "full";
      else if (q.obtainedMarks === 0) return "none";
      return "partial";
    };

    if (!displayData) {
      return (
        <div style={{ padding: "40px", textAlign: "center", color: "#8c8c8c" }}>
          Loading evaluation data...
        </div>
      );
    }

    if (isMobile) {
      const selectedQuestion = questions.find(
        (q) => q.id === selectedQuestionId
      );
      const selectedSection = sections.find(
        (s) => s.sectionId === selectedQuestion?.sectionId
      );
      const selectedSubject =
        displayData &&
        displayData.subjects.find((sub) =>
          sub.sections.some((s) => s.id === selectedSection?.id)
        );

      return (
        <div style={{ padding: "12px", background: "#f8fbff" }}>
          <EvaluationHeader
            totalScore={displayData.totalScore}
            maxTotalScore={totalMarks}
            onTimeUp={onTimeUp}
            initialTimeInSeconds={remainingLockTimeInSeconds}
            obtainedMarks={obtainedMarks}
          />
          <div
            style={{
              position: "fixed",
              bottom: "80px",
              left: 0,
              right: 0,
              background: "#fff",
              padding: "16px 12px",
              borderTop: "1px solid #f0f0f0",
              zIndex: 99,
            }}
          >
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "12px",
                paddingBottom: "8px",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {questions.map((q: Question) => (
                <div
                  key={q.id}
                  onClick={() => {
                    setSelectedQuestionId(q.id);
                    setIsModalVisible(true);
                    if (onQuestionClick) {
                      onQuestionClick(q);
                    }
                  }}
                  style={{
                    minWidth: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${
                      selectedQuestionId === q.id ? "#1890ff" : "#f0f0f0"
                    }`,
                    background:
                      selectedQuestionId === q.id ? "#e6f7ff" : "#fff",
                    color: selectedQuestionId === q.id ? "#1890ff" : "#8c8c8c",
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Q{q.questionNumber || q.id}
                </div>
              ))}
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: "8px",
                fontSize: "12px",
                color: "#8c8c8c",
                fontWeight: 600,
              }}
            >
              Question{" "}
              {questions.findIndex((q) => q.id === selectedQuestionId) + 1} of{" "}
              {questions.length}
            </div>
          </div>

          <QuestionEvaluationModal
            visible={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            question={selectedQuestion || null}
            isParent={questions.some(
              (child) => child.parentQuestionId === selectedQuestion?.questionId
            )}
            subjectName={selectedSubject?.label || ""}
            sectionName={selectedSection?.title || ""}
            onMarksChange={(qId, pId, val) => {
              if (selectedSection?.id) {
                handleMarksChange(selectedSection.id, qId, pId, val);
              }
            }}
            onCommentChange={(qId, pId, val) => {
              if (selectedSection?.id) {
                handleCommentChange(selectedSection.id, qId, val);
              }
            }}
            onAttemptedChange={handleAttemptedChange}
            onAIEvaluatedMarksChange={(qId, pId, val) => {
              if (selectedSection?.id) {
                handleAIEvaluatedMarksChange(selectedSection.id, qId, pId, val);
              }
            }}
          />
        </div>
      );
    }

    return (
      <EvaluationContainer>
        <EvaluationHeader
          totalScore={displayData.totalScore}
          maxTotalScore={totalMarks}
          onTimeUp={onTimeUp}
          initialTimeInSeconds={remainingLockTimeInSeconds}
          obtainedMarks={obtainedMarks}
        />

        <StyledTabs defaultActiveKey={displayData.subjects[0]?.key}>
          {displayData.subjects.map((sub: SubjectSummary) => (
            <Tabs.TabPane
              tab={
                <SubjectBadge color={sub.statusColor}>
                  <span className="dot" />
                  <span>{sub.label}</span>
                  {sub.hasCheck && (
                    <CheckCircleFilled style={{ color: "#52c41a" }} />
                  )}
                  {sub.unreadCount && (
                    <span className="count">{sub.unreadCount}</span>
                  )}
                </SubjectBadge>
              }
              key={sub.key}
            >
              <SubjectContent
                subject={sub}
                allQuestionsData={questions}
                getStatus={getStatus}
                onMarksChange={handleMarksChange}
                onCommentChange={handleCommentChange}
                onAttemptedChange={handleAttemptedChange}
                onVerifyAIChange={handleVerifyAIChange}
                onAIEvaluatedMarksChange={handleAIEvaluatedMarksChange}
                onQuestionClick={onQuestionClick}
              />
            </Tabs.TabPane>
          ))}
        </StyledTabs>
      </EvaluationContainer>
    );
  }
);

StudentQuestionComponent.displayName = "StudentQuestionComponent";

export default StudentQuestionComponent;
